import { useEffect, useState } from "react";
import { api, adminAuth } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Star, Key } from "lucide-react";
import DiscountsTab from "./admin/DiscountsTab";
import AddProductTab from "./admin/AddProductTab";
import StorefrontTab from "./admin/StorefrontTab";
import OrdersTab from "./admin/OrdersTab";
import ProductsTab from "./admin/ProductsTab";
import PresetGroupsTab from "./admin/PresetGroupsTab";
import TaxExemptTab from "./admin/TaxExemptTab";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [pwd, setPwd] = useState("");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("orders");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) { toast.error("New passwords don't match"); return; }
    if (pwdForm.next.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      const r = await api.patch("/admin/password", { current_password: pwdForm.current, new_password: pwdForm.next }, { headers: adminAuth() });
      localStorage.setItem("admin_token", r.data.token);
      setToken(r.data.token);
      setShowPwdModal(false);
      setPwdForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(e.response?.data?.detail || "Change failed"); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, m, p] = await Promise.all([
        api.get("/admin/orders", { headers: adminAuth() }),
        api.get("/admin/metrics", { headers: adminAuth() }),
        api.get("/admin/products", { headers: adminAuth() }),
      ]);
      setOrders(o.data);
      setMetrics(m.data);
      setProducts(p.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired");
        localStorage.removeItem("admin_token");
        setToken("");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { password: pwd });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--navy)] grid place-items-center p-6" data-testid="admin-login">
        <form onSubmit={handleLogin} className="bg-[var(--bone)] p-10 rounded-sm w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 grid place-items-center rounded-sm bg-[var(--navy)] text-[var(--bone)] font-serif-display"><Star className="w-5 h-5"/></div>
            <div>
              <div className="font-serif-display text-2xl text-[var(--navy)]">Papa&apos;s Office</div>
              <div className="font-mono-ui text-[10px] tracking-[0.25em] text-[var(--ink-soft)]">SHOP MANAGEMENT</div>
            </div>
          </div>
          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] block mb-2">Password</span>
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="input-field" data-testid="admin-password-input"/>
          </label>
          <button className="btn-primary w-full justify-center mt-6" data-testid="admin-login-button">Open the Shop</button>
          <button type="button" onClick={async () => {
            const email = window.prompt("Recovery email on file:");
            if (!email) return;
            try { await api.post("/admin/forgot", { email }); toast.success("If that email matches, a reset link has been sent"); }
            catch { toast.error("Request failed"); }
          }} className="block w-full text-center mt-3 font-mono-ui text-[10px] tracking-[0.25em] text-[var(--crimson)] hover:underline" data-testid="forgot-password-link">FORGOT PASSWORD?</button>
          <Link to="/" className="block text-center mt-4 font-mono-ui text-[10px] tracking-[0.25em] text-[var(--ink-soft)]">← BACK TO STORE</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="admin-dashboard">
      <header className="bg-[var(--navy)] text-[var(--bone)] border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 grid place-items-center bg-[var(--bone)] text-[var(--navy)] rounded-sm font-serif-display font-bold">G</div>
            <div className="font-serif-display text-xl">Papa G and MawMaw&apos;s Shop · Console</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPwdModal(true)} className="font-mono-ui text-[10px] tracking-[0.25em] flex items-center gap-2 px-3 py-2 hover:bg-white/5 rounded-sm" data-testid="admin-change-pwd"><Key className="w-3.5 h-3.5"/> PASSWORD</button>
            <button onClick={fetchAll} className="font-mono-ui text-[10px] tracking-[0.25em] flex items-center gap-2 px-3 py-2 hover:bg-white/5 rounded-sm" data-testid="admin-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> REFRESH</button>
            <button onClick={handleLogout} className="font-mono-ui text-[10px] tracking-[0.25em] flex items-center gap-2 px-3 py-2 hover:bg-white/5 rounded-sm" data-testid="admin-logout"><LogOut className="w-3.5 h-3.5"/> SIGN OUT</button>
          </div>
        </div>
      </header>

      {showPwdModal && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center z-50 p-6" onClick={() => setShowPwdModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={changePassword} className="bg-[var(--bone)] p-8 rounded-sm w-full max-w-md" data-testid="admin-pwd-modal">
            <div className="font-serif-display text-3xl text-[var(--navy)]">Change Password</div>
            <div className="font-mono-ui text-[10px] tracking-[0.25em] text-[var(--ink-soft)] mt-1">MIN 6 CHARACTERS</div>
            <div className="space-y-3 mt-5">
              <input type="password" placeholder="Current password" value={pwdForm.current} onChange={(e)=>setPwdForm({...pwdForm, current: e.target.value})} className="input-field" data-testid="pwd-current"/>
              <input type="password" placeholder="New password" value={pwdForm.next} onChange={(e)=>setPwdForm({...pwdForm, next: e.target.value})} className="input-field" data-testid="pwd-new"/>
              <input type="password" placeholder="Confirm new password" value={pwdForm.confirm} onChange={(e)=>setPwdForm({...pwdForm, confirm: e.target.value})} className="input-field" data-testid="pwd-confirm"/>
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setShowPwdModal(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-primary flex-1 justify-center" data-testid="pwd-submit">Save</button>
            </div>
          </form>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Metric label="Total Orders" value={metrics?.total_orders ?? "—"} testId="metric-total-orders"/>
          <Metric label="Paid Orders" value={metrics?.paid_orders ?? "—"} testId="metric-paid-orders"/>
          <Metric label="Awaiting Payment" value={metrics?.awaiting_payment_orders ?? "—"} testId="metric-awaiting-payment"/>
          <Metric label="Revenue" value={metrics ? `$${metrics.revenue.toFixed(2)}` : "—"} testId="metric-revenue" accent/>
        </div>

        <div className="flex gap-2 mb-6 font-mono-ui text-[10px] tracking-[0.25em] uppercase">
          {[
            { key: "orders", label: "Orders" },
            { key: "products", label: "Products" },
            { key: "add", label: "Add Product" },
            { key: "presets", label: "Preset Sets" },
            { key: "storefront", label: "Storefront" },
            { key: "discounts", label: "Discounts" },
            { key: "tax-exempt", label: "Tax Exempt" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2.5 rounded-sm border transition ${tab === t.key ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersTab orders={orders} setOrders={setOrders} />}
        {tab === "products" && <ProductsTab products={products} setProducts={setProducts} />}
        {tab === "add" && <AddProductTab products={products} onCreated={fetchAll} goToProducts={() => setTab("products")} />}
        {tab === "presets" && <PresetGroupsTab products={products} onChanged={fetchAll} />}
        {tab === "storefront" && <StorefrontTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "tax-exempt" && <TaxExemptTab />}
      </div>
    </div>
  );
}

function Metric({ label, value, accent, testId }) {
  return (
    <div className={`card-rustic p-6 ${accent ? "border-[var(--crimson)]/30" : ""}`} data-testid={testId}>
      <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">{label}</div>
      <div className={`font-serif-display text-4xl mt-2 ${accent ? "text-[var(--crimson)]" : "text-[var(--navy)]"}`}>{value}</div>
    </div>
  );
}
